// Packages, trials and subscriptions (migration 043): the feature catalogue, which screens and API routes each
// feature covers, and the shapes the platform portal and the owner's package screen exchange with the server.
//
// Effective access to anything = the organization is active (sign-in refuses others) AND its package includes the
// feature AND the user holds the permission keys AND the record is in the user's branch/warehouse/treasury scope.
// A package enables a feature for the business; it gives no employee any permission, and no permission (nor the
// administrator flag) unlocks a feature the package leaves out. The server checks the feature on every request
// (featureForApi); the web app uses featureForRoute only to hide menus and explain refusals.

/**
 * The modules a package switches on or off (migration 046, request of 29 Oct 2026, item 1). Until then only POS and
 * Fixed assets could be switched; the rest was one bundled core. Now each is its own switch:
 * - accounting: the accounting screens (chart, journal entries, cost centers, partners, opening balances). The
 *   accounting engine is not a module: sales, purchases, vouchers, POS and assets post their journal entries whatever
 *   the package says, so the books stay complete.
 * - sales (sales invoices, refunds, quotations, customers, price controls), purchases (purchase invoices, refunds,
 *   suppliers), stock (items and stock: items, categories, units, item kinds, stock documents), vouchers (receipts and
 *   payments: expenses, deposits, withdrawals, collections, payments, cash transfers), reports (every report screen and
 *   export), einvoice (the e-invoicing screens: documents, XML, chain check), printing (printed documents, printers,
 *   the print agent), pos, fixed_assets.
 * E-invoicing as an obligation is not switched off with its screens: every sale and refund posted still gets its
 * e-invoice document (counter, hash chain, QR) in the posting's transaction (rules Z-1 to Z-6).
 */
export type FeatureKey = "accounting" | "sales" | "purchases" | "stock" | "vouchers" | "reports" | "einvoice" | "printing" | "pos" | "fixed_assets";
export const FEATURES: readonly FeatureKey[] = ["accounting", "sales", "purchases", "stock", "vouchers", "reports", "einvoice", "printing", "pos", "fixed_assets"];
/**
 * The modules that were the bundled core before migration 046. Terms written before it, or by an older build that
 * does not know them (no core module at all in the list), include all of them; migration 046 and the database's
 * triggers make that explicit, so a package keeps exactly what it gave.
 */
export const CORE_MODULES: readonly FeatureKey[] = ["accounting", "sales", "purchases", "stock", "vouchers", "reports", "einvoice", "printing"];
/** What each module needs: a package with a module includes what it depends on, or is refused (module_dependency). */
export const MODULE_DEPENDENCIES: Record<FeatureKey, readonly FeatureKey[]> = {
  accounting: [], sales: ["stock"], purchases: ["stock"], stock: [], vouchers: [], reports: [], einvoice: ["sales"], printing: [],
  pos: ["sales", "stock", "printing"], fixed_assets: []
};
/**
 * A list naming no core module is read as older builds and clients meant it: the whole core plus what it names (as the
 * database does, migration 046). So a package always includes at least one core module.
 */
export function expandModules(features: readonly FeatureKey[]): FeatureKey[] {
  const has = new Set(features);
  if (!CORE_MODULES.some((m) => has.has(m))) for (const m of CORE_MODULES) has.add(m);
  return FEATURES.filter((f) => has.has(f));
}
/** Dependencies left out, as `<module>:<needs>` (empty when the combination is supported). */
export function moduleProblems(features: readonly FeatureKey[]): string[] {
  const has = new Set(features), out: string[] = [];
  for (const f of FEATURES) if (has.has(f)) for (const d of MODULE_DEPENDENCIES[f]) if (!has.has(d)) out.push(`${f}:${d}`);
  return out;
}
/** The modules that depend on `f` (to explain why it cannot be switched off alone). */
export const dependentsOf = (f: FeatureKey): FeatureKey[] => FEATURES.filter((x) => MODULE_DEPENDENCIES[x].includes(f));

export type LimitKey = "users" | "branches" | "warehouses" | "treasuries" | "posDevices";
export const LIMIT_KEYS: readonly LimitKey[] = ["users", "branches", "warehouses", "treasuries", "posDevices"];
export type Limits = Record<LimitKey, number>;

// Screens of each module (web paths). A screen may need more than one (an asset report: reports and fixed assets).
// Screens shared by several modules (invoice pages by id, settings that link accounts) stay open; the server refuses
// what the package leaves out when the record is read.
const ROUTE_FEATURES: [RegExp, FeatureKey][] = [
  [/^\/pos(?!\/printers$)(\/|$)|^\/settings\/pos-payment-buttons$|^\/settings\/pos-devices$/, "pos"],
  [/^\/assets(\/|$)|^\/assets-purchase\/|^\/reports\/(all-assets|asset-depreciation|asset-invoices|asset-invoice-details|asset-tax)$/, "fixed_assets"],
  [/^\/settings\/(chart-template|accountIndex|accountTypes)$|^\/chart$|^\/account\/|^\/cost-centers$|^\/journals(\/|$)|^\/legacy-history(\/|$)|^\/opening-balances?(\/|$)|^\/partners$/, "accounting"],
  [/^\/invoices\/(Sale|SaleRefund|Quotation)$|^\/price-controls$|^\/Customer(\/|$)|^\/settings\/tobacco$|^\/reports\/tobacco-fees$/, "sales"],
  [/^\/invoices\/(Purchase|PurchaseRefund)$|^\/Supplier(\/|$)/, "purchases"],
  [/^\/(item-kinds|item-Category|unitsConfig|warehouse-stock)$|^\/items\/|^\/invoices\/(BeginningInventory|Corrupted|Transfer|Inventory)$|^\/warehouses\/(inventory|transfers|depreciation)\//, "stock"],
  [/^\/(Expense|transfers)$|^\/receipts\/|^\/receipt\/|^\/transfer\//, "vouchers"],
  [/^\/reports(\/|$)/, "reports"],
  [/^\/printers(\/|$)|^\/invoices\/print\/|^\/items\/qrPrint$|^\/pos\/printers$/, "printing"]
];
/** Every module a screen needs (empty: open to every package). */
export function featuresForRoute(path: string): FeatureKey[] {
  return [...new Set(ROUTE_FEATURES.filter(([re]) => re.test(path)).map(([, f]) => f))];
}
/** The first module a screen needs, or null. */
export function featureForRoute(path: string): FeatureKey | null {
  return featuresForRoute(path)[0] ?? null;
}

// API routes of each module, matched on the route pattern (Fastify's routeOptions.url, with the /api prefix). Routes
// shared by several modules (/api/invoices, whose kind decides; /api/vouchers/options, used by vouchers and opening
// balances; /api/accounting/options, the account lists of settings forms) are checked by their services instead.
const API_FEATURES: [RegExp, FeatureKey][] = [
  [/^\/api\/pos(\/|$)|^\/api\/settings\/pos-quick-pay(\/|$)/, "pos"],
  [/^\/api\/(assets|asset-kinds|asset-invoices|asset-depreciations)(\/|$)|^\/api\/reports\/(assets|asset-depreciation|asset-invoices|asset-invoice-details|asset-tax)$/, "fixed_assets"],
  [/^\/api\/(accounts|account-types|account-levels|chart-templates|journal-entries|legacy-documents|cost-centers|partners|opening-balances)(\/|$)|^\/api\/accounting\/(tree|tree-search|next-code|numbering|config-references)(\/|$)|^\/api\/ledger\//, "accounting"],
  [/^\/api\/(customers|sales-rights|below-cost-approvals)(\/|$)|^\/api\/invoices\/:id\/legal-reference|^\/api\/settings\/tobacco(\/|$)|^\/api\/reports\/tobacco-fees$/, "sales"],
  [/^\/api\/suppliers(\/|$)/, "purchases"],
  [/^\/api\/(items|item-categories|units|item-kinds|stock-documents|stock)(\/|$)/, "stock"],
  [/^\/api\/(money-vouchers|cash-transfers|settlements)(\/|$)/, "vouchers"],
  [/^\/api\/reports\//, "reports"],
  [/^\/api\/zatca\//, "einvoice"],
  [/^\/api\/(printers|prints)(\/|$)|^\/api\/invoices\/:id\/(print-header|prints)$|^\/api\/items\/barcode-print$|^\/api\/pos\/agent-download(\/|$)/, "printing"]
];
// The print agent download is printing, not POS: back-office printing uses it too.
const API_NOT_POS = /^\/api\/pos\/agent-download(\/|$)/;
/** Every module an API route needs (empty: core, or checked by its service). */
export function featuresForApi(routeUrl: string): FeatureKey[] {
  const out = API_FEATURES.filter(([re, f]) => re.test(routeUrl) && !(f === "pos" && API_NOT_POS.test(routeUrl))).map(([, f]) => f);
  return [...new Set(out)];
}
/** The first module an API route needs, or null. */
export function featureForApi(routeUrl: string): FeatureKey | null {
  return featuresForApi(routeUrl)[0] ?? null;
}

export type PackageKind = "trial" | "paid";
export type BillingPeriod = "monthly" | "quarterly" | "yearly";

/** A package as the Super Admin edits it. Price, currency and billing period are optional for a paid package. */
export interface PackageInput {
  code: string; nameEn: string; nameAr: string; descriptionEn: string; descriptionAr: string;
  active: boolean; public: boolean; kind: PackageKind; trialDays: number | null;
  billingPeriod: BillingPeriod | null; currency: string | null; price: string | null;
  features: FeatureKey[];
  maxUsers: number; maxBranches: number; maxWarehouses: number; maxTreasuries: number; maxPosDevices: number;
  /** Why the terms changed: recorded with the new version. */
  note?: string;
  /**
   * Show on the marketing website (migration 057). Only an active, public package can be shown; making it private or
   * inactive takes it off the website. Left out by an older screen: kept as it is. Display only: never a new version.
   */
  website?: boolean;
}

/** Terms of one version of a package. */
export interface PackageTerms {
  kind: PackageKind; trialDays: number | null; billingPeriod: BillingPeriod | null; currency: string | null; price: string | null;
  features: FeatureKey[]; limits: Limits;
}

export type SubscriptionPayment = "none" | "recorded_manual";
export type ChangeKind = "grant" | "trial" | "renewal" | "change";

/**
 * Where a signed-up business stands today, one state only: trial or paid (a current subscription of that kind),
 * expired (it had a subscription and none is current), none (never had one). Provisioned organizations are not on
 * packages. scheduled is shown beside the state, never instead of it.
 */
export type PackageState = "trial" | "paid" | "expired" | "none" | "provisioned";

export interface MySubscriptionDto {
  id: string; kind: PackageKind; status: "active" | "cancelled"; startsOn: string; endsOn: string; originalEndsOn: string | null;
  changeKind: ChangeKind; payment: SubscriptionPayment; version: number; cancelledAt: string | null;
  plan: { id: string; code: string; nameEn: string; nameAr: string; descriptionEn: string; descriptionAr: string };
  terms: PackageTerms;
}

export interface PublicPackageDto {
  id: string; code: string; nameEn: string; nameAr: string; descriptionEn: string; descriptionAr: string;
  kind: PackageKind; trialDays: number | null; billingPeriod: BillingPeriod | null; currency: string | null; price: string | null;
  features: FeatureKey[]; limits: Limits;
}

export interface SubscriptionRequestDto {
  id: string; kind: "renewal" | "change"; plan: { id: string; nameEn: string; nameAr: string } | null; note: string;
  status: "open" | "done" | "declined" | "withdrawn"; createdAt: string; requestedBy: string; handledAt: string | null; response: string | null;
}

/** The owner's package screen (Settings › Package). */
export interface MyPackageDto {
  state: PackageState;
  today: string;
  /** The subscription in force today, if any. */
  current: MySubscriptionDto | null;
  /** Days left including today (current subscription only). */
  daysLeft: number | null;
  /** True when the current subscription ends within the warning window and nothing follows it. */
  expiringSoon: boolean;
  warningDays: number;
  /** Subscriptions that start later (scheduled changes and renewals). */
  scheduled: MySubscriptionDto[];
  /** Every subscription, newest first. */
  history: MySubscriptionDto[];
  /** What applies with no current subscription. */
  noSubscription: { features: FeatureKey[]; limits: Limits };
  /** In force today: features and limits. */
  effective: { features: FeatureKey[]; limits: Limits | null };
  usage: Limits;
  trialUsed: boolean;
  packages: PublicPackageDto[];
  openRequest: SubscriptionRequestDto | null;
  requests: SubscriptionRequestDto[];
  /** Only the owner asks for a renewal or a change. */
  canRequest: boolean;
  /** Migration 044: open, or expired / none when the software is closed until a renewal or a first package. */
  access: PackageAccess;
  /** Migration 044: legacy (the earlier rules, kept for businesses that existed) or enforced (no package = no access). */
  rules: "legacy" | "enforced";
}

export interface SubscriptionRequestInput { requestId: string; kind: "renewal" | "change"; planId?: string | null; note?: string }

/** The short form of a business's package, for the menu and the expiry warning. */
export interface MePackageDto {
  state: PackageState; features: FeatureKey[]; endsOn: string | null; daysLeft: number | null; expiringSoon: boolean; kind: PackageKind | null;
  /**
   * Migration 044: open, or expired / none when the business is under package rules and no trial or subscription
   * covers today. Anything but open closes the software; the owner keeps the package screen and renewal requests.
   */
  access: PackageAccess;
}

export type PackageAccess = "open" | "expired" | "none";

/** The signup page's offer: shown only when a Super Admin published an active, public trial package. */
export interface SignupOfferDto { nameEn: string; nameAr: string; descriptionEn: string; descriptionAr: string; trialDays: number; features: FeatureKey[] }

// ---------------------------------------------------------------- Super Admin dashboard
export interface PlatformDashboardDto {
  generatedAt: string; today: string; window: number;
  /** By organization status; every organization counted once. */
  businesses: { total: number; pending: number; active: number; suspended: number; rejected: number; expired: number };
  /**
   * Signed-up organizations that are not rejected, each in exactly one package state; provisioned organizations
   * are counted apart. paidRecorded: of the paid ones, those whose current subscription has a manually recorded payment.
   */
  packages: { population: number; trial: number; paid: number; paidRecorded: number; expired: number; none: number; provisioned: number; scheduled: number };
  expiring: { orgId: string; nameEn: string; nameAr: string; kind: PackageKind; plan: string; endsOn: string; daysLeft: number }[];
  recentSignups: { orgId: string; businessName: string; status: string; reviewMode: string; createdAt: string }[];
  openRequests: number;
  /** Migration 044: active signed-up businesses closed today (no trial or subscription in force), and those under legacy rules. */
  access: { blocked: number; legacy: number; /** On the earlier rules with a trial in force: the trial's end will not close them (started before this build required the move to package rules). */ legacyTrial: number };
  /** The trial package new trials start from by default (code 'demo'); its length is edited on Packages, for future trials only. */
  trialDefault: { planId: string; code: string; nameEn: string; nameAr: string; trialDays: number | null; version: number; active: boolean } | null;
  events: { id: string; at: string; action: string; actor: string | null; organizationId: string | null; organizationName: string | null; reason: string | null }[];
  /** Migration 048: resellers, the businesses they manage, those whose terms are not set, and what resellers owe Delta per currency (wholesale; never customers' data). */
  resellers: { active: number; suspended: number; customers: number; termsMissing: number; owed: { currency: string; amount: string }[] };
}

export interface PlatformPackageDto extends PackageInput {
  id: string; version: number; updatedAt: string; website: boolean;
  /** Subscriptions of any state; current ones on the latest version and on older versions. */
  subscriptions: number; currentOnLatest: number; currentOnOlder: number;
  /** True when this package is the published signup offer. */
  signupOffer: boolean;
}

export interface PackageVersionDto extends PackageTerms { version: number; createdAt: string; createdBy: string | null; note: string | null; subscriptions: number }
